import { Composite } from '@floating-ui/react';
import classNames from 'classnames';
import {
  Children,
  cloneElement,
  isValidElement,
  useMemo,
  useRef,
  type JSX,
} from 'react';
import { useDiveContext } from '../../context';
import { omitProps } from '../../helpers/helpers';
import { useOverflowDropdownItems } from '../../hooks/useOverflowDropdownItems';
import { useScrollIntoView } from '../../hooks/useScrollIntoView';
import { navListCVA } from './DiNav.cva';
import type { DiNavProps } from './DiNav.types';
import type { DiNavDropdownProps } from './DiNavDropdown/DiNavDropdown.types';
import type { DiNavItemProps } from './DiNavItem/DiNavItem.types';

/**
 * **Dive Internal Nav**: an unstyled/headless component designed to provide the foundational structure for building
 * higher-level components such as `HeaderNav` and `Tabs`.
 *
 * ### Key Features:
 * - Uses the {@link https://floating-ui.com/docs/react | floating-ui/react} for robust accessibility and positioning.
 *
 * @example
 *
 * ```tsx
 * <DiNav
 *   activeIndex={0}
 *   dropdownLabel="More"
 *   overflow="dropdown"
 * >
 *   <NavItem label="Home" />
 *   <NavItem label="About" />
 *   <NavItem label="Contact" />
 * </DiNav>
 * ```
 */
export function DiNav({
  activeIndex = -1,
  className,
  dropdownElement,
  id,
  shouldCenterActiveItem = true,
  navListClassName,
  role = 'menubar',
  overflow = 'scroll',
  'data-testid': dataTestId,
  children,
  ...a11y
}: DiNavProps): JSX.Element {
  const { isTv } = useDiveContext();

  const navRef = useRef<HTMLElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);
  const invisibleUlRef = useRef<HTMLUListElement>(null);
  const liDropdownRef = useRef<HTMLLIElement>(null);

  const hasDropdown = overflow === 'dropdown' && !!dropdownElement;

  const navItemProps = useMemo(() => {
    const validChildren = Children.toArray(children).filter(
      isValidElement<DiNavItemProps>,
    );
    return validChildren.map((child) =>
      omitProps(child.props, ['outerWrapper']),
    );
  }, [children]);

  const { overflowingItems, visibleItems } = useOverflowDropdownItems({
    containerRef: navRef,
    dropdownRef: liDropdownRef,
    enabled: hasDropdown,
    items: navItemProps,
    measurableRef: invisibleUlRef,
  });

  useScrollIntoView({
    containerRef: ulRef,
    index: activeIndex,
    enabled: shouldCenterActiveItem,
  });

  const overflowCurrentIndex = activeIndex - visibleItems.length;

  /**
   * Indices of the items that are in the overflow dropdown. Disabled in the Composite component,
   * to avoid them being focusable or navigable horizontally (Bug).
   */
  const isDropdownInvisible = !overflowingItems.length;
  const overflowIndices = Array.from(
    { length: overflowingItems.length },
    (_, i) => i + visibleItems.length + 1,
  );
  const dropdownIndex = visibleItems.length;
  const disabledIndices = isDropdownInvisible
    ? [dropdownIndex]
    : overflowIndices;

  return (
    <nav
      id={id}
      ref={navRef}
      data-testid={dataTestId}
      className={classNames('relative w-full box-border', className)}
      {...a11y}
    >
      {
        /* Invisible reference list for dynamic overflow calculation.
        Acts as a size reference for the visible list to identify which items to collapse into the dropdown */
        hasDropdown && (
          <ul
            ref={invisibleUlRef}
            className={classNames(
              'absolute invisible z-0 list-none m-dt-spacing-none p-dt-spacing-none flex',
              navListClassName,
            )}
            aria-hidden
            tabIndex={-1}
          >
            {children}
          </ul>
        )
      }

      {/* Real visible list */}
      <Composite
        loop={!isTv}
        aria-label={a11y?.['aria-label']}
        className={classNames(
          'list-none m-dt-spacing-none p-dt-spacing-none flex',
          navListCVA({ overflow, hasDropdown }),
          navListClassName,
        )}
        orientation="horizontal"
        ref={ulRef}
        render={<ul />}
        role={role}
        disabledIndices={hasDropdown ? disabledIndices : undefined}
      >
        {hasDropdown && overflowingItems.length > 0
          ? Children.toArray(children).slice(0, visibleItems.length)
          : children}

        {hasDropdown && (
          <li
            role="none"
            ref={liDropdownRef}
            {...(!overflowingItems.length && {
              'aria-hidden': true,
              className: 'invisible absolute left-0', // Only for measuring DOM width of the dropdown trigger
            })}
          >
            {cloneElement<DiNavDropdownProps>(dropdownElement, {
              items: overflowingItems,
              currentIndex: overflowCurrentIndex,
            })}
          </li>
        )}
      </Composite>
    </nav>
  );
}
